import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { CompanySessionSummary, DayEndService, DayEndResult, DayEndRun } from './DayEndService';

/** Trigger SOD for a branch. Returns result + loading/error state. */
export const useTriggerSod = (branch: string) => {
    const [result, setResult] = useState<DayEndResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trigger = useCallback(async (targetDate?: string | null) => {
        setLoading(true);
        setError(null);
        try {
            const res = await DayEndService.runSod(branch, targetDate);
            setResult(res);
            message.success('Start-of-Day completed successfully');
            return res;
        } catch (err: any) {
            const status = err.response?.status;
            const msg = err.response?.data?.error || err.message || 'SOD failed';
            if (status === 409) {
                message.warning('SOD already run today for this branch');
            } else {
                message.error(msg);
            }
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, [branch]);

    return { trigger, result, loading, error };
};

/** Trigger EOD for a branch. Returns result + loading/error state. */
export const useTriggerEod = (branch: string) => {
    const [result, setResult] = useState<DayEndResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trigger = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await DayEndService.runEod(branch);
            setResult(res);
            message.success('End-of-Day completed successfully');
            return res;
        } catch (err: any) {
            const status = err.response?.status;
            const msg = err.response?.data?.error || err.message || 'EOD failed';
            if (status === 409) {
                message.warning('EOD already run today for this branch');
            } else {
                message.error(msg);
            }
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, [branch]);

    return { trigger, result, loading, error };
};

/** Fetch last 30 day-end runs for a branch (React Query). */
export const useRunHistory = (branch: string) =>
    useQuery<DayEndRun[]>({
        queryKey: ['day-end-history', branch],
        queryFn: () => DayEndService.getHistory(branch),
        staleTime: 30_000,
        enabled: !!branch,
    });

export const useSessionSummary = (branch: string) =>
    useQuery<CompanySessionSummary>({
        queryKey: ['day-end-summary', branch],
        queryFn: () => DayEndService.getSummary(branch),
        staleTime: 10_000,
        enabled: !!branch,
    });

/** Fetch run status for a specific date (React Query). */
export const useDayStatus = (branch: string, date: string) =>
    useQuery<DayEndRun | null>({
        queryKey: ['day-end-status', branch, date],
        queryFn: () => DayEndService.getStatusForDate(branch, date),
        staleTime: 30_000,
        enabled: !!branch && !!date,
    });
